import withLifecycles from 'async-lifecycles';
import produce from 'immer';
import React, {Component, Fragment} from 'react';
import {PortalWithState} from 'react-portal';
import {Spring} from 'react-spring/renderprops';
import Blacklist from '../../store/services/blacklist';
import {
  Button,
  Header,
  Icon,
  Table,
  Popup,
  Label,
  Input,
  Pagination,
  Item,
} from 'semantic-ui-react';
import {Form} from 'semantic-ui-react';
import toastr from 'toastr';
import {connect} from 'react-redux';
import {debounce} from 'lodash';

import searchImage from '../assets/search.svg';
import usersApi from '../../store/services/users';
import FormContainer from '../form-components/FormContainer';
import Layout from '../layout/Layout';
import LoadingContainer from '../layout/LoadingContainer';
import Page from '../layout/Page';
import {ModalWrapper} from '../modal/ModalWrapper';
import BlacklistForm from './BlacklistForm';
import RequiresRole, {ROLES} from '../security/RequiresRole';
import {ErrorMessages} from '../form-components/ErrorMessages';
import styled from 'react-emotion';

const Styles = styled.div`
  .tableHeader {
    background-color: '#f00';
    display: flex;
    flex-grow: 1;
    flex-direction: row;
    justify-content: space-between;

    .ui.header {
      margin-bottom: 0;
    }
  }
`;

class AllBlacklistsTable extends Component {
  state = {
    loading: false,
    pageInfo: {},
    blacklist: undefined,
    orderByName: false,
    activePage: 1,
    searchTerm: '',
  };

  _mutate = cb => {
    this.setState(produce(cb));
  };

  componentDidMount() {
    this._loadBlacklists(0, '');
  }

  _deleteBlacklist = async blacklist => {
    try {
      await Blacklist.unblacklist(blacklist.id);
      if (blacklist.userId) {
        let user = await usersApi.fetchById(blacklist.userId);
        user.result.isBlacklisted = false;
        await usersApi.update(user.result);
      }
      toastr.info('Blacklist removed');
      this._loadBlacklists(0, '');
      this.setState({searchTerm: ''});
      this._closePortal();
    } catch (error) {
      toastr.warning('Error removing blacklist');
    }
  };

  _loadBlacklists = (pageNumber, searchTerm) => {
    withLifecycles(
      () =>
        Blacklist.fetch({
          request: {
            page: pageNumber,
            pageSize: 20,
          },
          searchTerm: searchTerm,
          orderByName: this.state.orderByName,
        }),
      ({loading, data, error}) => {
        this.setState({activePage: pageNumber + 1});
        this.setState(
          produce(draft => {
            draft.loading = loading;

            if (data && !data.hasErrors) {
              draft.pageInfo = data.result;
            }
          })
        );
      }
    );
  };

  _save = () => {
    withLifecycles(
      () => Blacklist.update(this.state.blacklist),
      ({loading, data, error}) => {
        this.setState(
          produce(draft => {
            draft.saving = loading;
            draft.error = error;

            if (data && !data.hasErrors) {
              this.props.history.push('/manage/blacklists');
              toastr.success('Blacklist Updated');
              this._loadBlacklists(0, '');
              this.setState({searchTerm: ''});
              this._closePortal();
            }
          })
        );
      }
    );
  };

  _showDetail = blacklist => {
    this._openPortal();
    this.setState({blacklist, error: {}});
  };

  _handleTextChanged = (event, {name, value}) => {
    this._mutate(draft => {
      draft.blacklist[name] = value;
    });
  };

  render() {
    const {pageInfo, activePage} = this.state;
    const blacklists = pageInfo.data || [];
    return (
      <FormContainer loading={this.state.loading}>
        <Styles>
          <div className="tableHeader">
            <Header as="h1">Blacklists</Header>
            <div>
              <Input
                placeholder="Search Blacklist..."
                onChange={debounce((e, searchTerm) => {
                  this._loadBlacklists(0, searchTerm.value);
                  this.setState({searchTerm: searchTerm.value});
                }, 400)}
              />
            </div>
          </div>
        </Styles>
        {pageInfo.pageCount > 0 && (
          <PortalWithState closeOnEsc>
            {({openPortal, closePortal, portal}) => {
              this._closePortal = closePortal;
              this._openPortal = openPortal;
              return (
                <Fragment>
                  <Table basic="very" selectable>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell />

                        <Table.HeaderCell>Customer</Table.HeaderCell>
                        <Table.HeaderCell>First Name</Table.HeaderCell>
                        <Table.HeaderCell>Last Name</Table.HeaderCell>
                        <Table.HeaderCell>Email Address</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {blacklists.map(blacklists =>
                        this._renderRow(blacklists)
                      )}
                    </Table.Body>
                  </Table>
                  <Pagination
                    activePage={activePage}
                    onPageChange={async (e, paginationInfo) => {
                      await this._loadBlacklists(
                        paginationInfo.activePage - 1,
                        this.state.searchTerm
                      );
                    }}
                    ellipsisItem={{
                      content: <Icon name="ellipsis horizontal" />,
                      icon: true,
                    }}
                    firstItem={{
                      content: <Icon name="angle double left" />,
                      icon: true,
                    }}
                    lastItem={{
                      content: <Icon name="angle double right" />,
                      icon: true,
                    }}
                    prevItem={{content: <Icon name="angle left" />, icon: true}}
                    nextItem={{
                      content: <Icon name="angle right" />,
                      icon: true,
                    }}
                    totalPages={pageInfo.pageCount}
                  />
                  {portal(this._renderDetailModal())}
                </Fragment>
              );
            }}
          </PortalWithState>
        )}
        {blacklists.length === 0 && (
          <Item.Group>
            <Item>
              <Item.Image size="mini" src={searchImage} />
              <Item.Content verticalAlign="middle">
                <strong>No Blacklists.</strong>
                <div>
                  <small>
                    No blacklists match your search results. Please clear or
                    refine your search.
                  </small>
                </div>
              </Item.Content>
            </Item>
          </Item.Group>
        )}
      </FormContainer>
    );
  }

  _renderRow = blacklist => (
    <Table.Row key={blacklist.id}>
      <Table.Cell collapsing>
        <Button
          className="clear"
          icon
          onClick={() => this._showDetail(blacklist)}
        >
          <Icon name="edit" />
        </Button>
      </Table.Cell>
      <RequiresRole roles={[ROLES.SYSTEM_ADMIN, ROLES.MM_ADMIN]}>
        <Table.Cell>
          {blacklist.customer && (
            <div>
              {blacklist.customer}
              <Label
                size="mini"
                style={{
                  backgroundColor: '#e8c164',
                  color: '#FFF',
                  marginLeft: '5px',
                }}
              >
                Customer
              </Label>
            </div>
          )}
        </Table.Cell>
      </RequiresRole>
      <Table.Cell>{blacklist.firstName}</Table.Cell>
      <Table.Cell>{blacklist.lastName}</Table.Cell>
      <Table.Cell>{blacklist.emailAddress}</Table.Cell>
    </Table.Row>
  );

  _renderDetailModal = () => {
    const {blacklist, error = {}} = this.state;

    return (
      <Spring from={{right: '-100%'}} to={{right: '0%'}}>
        {styles => (
          <ModalWrapper style={styles}>
            <Layout className="page-wrapper">
              <Header className="page-header">
                <Page.HeaderWithActions title="Blacklisted User Info">
                  <Button
                    icon="close"
                    size="mini"
                    onClick={this._closePortal}
                  />
                </Page.HeaderWithActions>
              </Header>
              <Layout.Fill scroll padded>
                <LoadingContainer hideContentWhenLoading>
                  <Form onSubmit={this._save}>
                    {blacklist.userId === null && (
                      <BlacklistForm
                        blacklist={blacklist}
                        handleTextChanged={this._handleTextChanged}
                        isReadOnly={false}
                        isBeingEdited={true}
                      />
                    )}
                    {blacklist.userId === null && (
                      <Button type="Submit" primary>
                        Update
                      </Button>
                    )}
                    {blacklist.userId !== null && (
                      <BlacklistForm
                        blacklist={blacklist}
                        handleTextChanged={this._handleTextChanged}
                        isReadOnly={true}
                        isBeingEdited={true}
                      />
                    )}

                    <ErrorMessages error={error} />

                    <Button basic onClick={this._closePortal}>
                      Cancel
                    </Button>

                    <Popup
                      trigger={
                        <Button basic icon floated="right" type="button">
                          Remove from Blacklist
                        </Button>
                      }
                      content={
                        <Button
                          type="button"
                          color="red"
                          content="Are you sure?"
                          onClick={() => this._deleteBlacklist(blacklist)}
                        />
                      }
                      on="click"
                      position="left center"
                    />
                  </Form>
                </LoadingContainer>
              </Layout.Fill>
            </Layout>
          </ModalWrapper>
        )}
      </Spring>
    );
  };
}

const mapStateToProps = ({customers, identity}) => ({
  customers: customers.data,
  role: identity.user.role,
});

export default connect(mapStateToProps)(AllBlacklistsTable);
